
import React from 'react';
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Music, Calendar, Video, Users } from 'lucide-react';

const Index = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-piano-black to-gray-900 text-white py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-pattern"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-xl animate-fade-in">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight mb-6">
                Bring musikken til live med <span className="text-piano-gold">professionel klaverundervisning</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Klaverundervisning for alle niveauer med Oscar Gilbert - uddannet fra Rytmisk Musikkonservatorium. Undervisning i Søborg eller online.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-piano-gold hover:bg-piano-gold/90 text-white">
                  <Link to="/kontakt">Gratis Prøvetime</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="bg-white border-white hover:bg-white/90 text-piano-black">
                  <Link to="/undervisning">Se Undervisningstilbud</Link>
                </Button>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="w-full h-[500px] rounded-lg shadow-2xl overflow-hidden">
                <img 
                  src="/lovable-uploads/645ce282-0307-4a13-a3a7-eb036180d6af.png" 
                  alt="Oscar Gilbert ved klaveret" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-piano-black">
              Personlig & professionel klaverundervisning tilpasset netop dig
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Oplev en dedikeret og inspirerende tilgang til klaverundervisning, der er skræddersyet til dine behov og musikalske drømme.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md text-center transition-transform hover:translate-y-[-5px]">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-piano-gold/10 text-piano-gold mb-6">
                <Music className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-serif font-semibold mb-4">Erfaren pianist</h3>
              <p className="text-gray-600">
                Undervisning af høj kvalitet fra en pianist uddannet fra Rytmisk Musikkonservatorium med solid erfaring.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-md text-center transition-transform hover:translate-y-[-5px]">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-piano-gold/10 text-piano-gold mb-6">
                <Users className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-serif font-semibold mb-4">Alle niveauer</h3>
              <p className="text-gray-600">
                Undervisning tilpasset din erfaring og dine mål - fra nybegyndere til øvede pianister.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-md text-center transition-transform hover:translate-y-[-5px]">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-piano-gold/10 text-piano-gold mb-6">
                <Video className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-serif font-semibold mb-4">Online undervisning</h3>
              <p className="text-gray-600">
                Fleksibel online undervisning der passer ind i din hverdag, uanset hvor du befinder dig.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-md text-center transition-transform hover:translate-y-[-5px]">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-piano-gold/10 text-piano-gold mb-6">
                <Calendar className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-serif font-semibold mb-4">Fleksibel booking</h3>
              <p className="text-gray-600">
                Vælg mellem enkelttimer eller faste tider for en hel sæson, alt efter dine behov.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6 text-piano-black">
                Mød Oscar Gilbert, <span className="text-piano-gold">din personlige klaverunderviser</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Med en baggrund fra Rytmisk Musikkonservatorium og mange års erfaring som både udøvende pianist og underviser, 
                har Oscar hjulpet hundreder af elever med at opnå deres musikalske mål.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Oscars pædagogiske tilgang kombinerer teknisk perfektion med musikalsk udtryk, så hver elev 
                udvikler deres eget unikke forhold til klaveret.
              </p>
              <Button asChild className="bg-piano-black hover:bg-piano-black/90 text-white">
                <Link to="/om">Læs Mere Om Oscar</Link>
              </Button>
            </div>
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative w-full max-w-lg">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-piano-gold to-amber-400 rounded-lg blur opacity-30"></div>
                <div className="relative bg-white p-4 rounded-lg shadow-xl">
                  <div className="aspect-[3/4] rounded-md overflow-hidden">
                    <img 
                      src="/lovable-uploads/c3099eea-1cdc-462a-9e3c-85f757436352.png" 
                      alt="Oscar Gilbert" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-piano-black">Succeshistorier fra tilfredse elever</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Læs hvad andre elever har oplevet med Oscars klaverundervisning.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Mette Hansen",
                text: "Oscar har en fantastisk evne til at forklare selv de mest komplekse teknikker på en let forståelig måde. Efter 6 måneder med ham har jeg gjort større fremskridt end i mine foregående 2 år."
              },
              {
                name: "Lars Petersen",
                text: "Jeg begyndte som fuldstændig nybegynder i en alder af 45, og jeg var nervøs for om det var for sent. Oscar har vist mig, at det aldrig er for sent at lære at spille klaver. Hans tålmodighed er utrolig."
              },
              {
                name: "Sofie Andersen",
                text: "Oscars online timer er fantastiske! Teknikken fungerer perfekt, og jeg føler, jeg får lige så meget ud af timerne, som hvis jeg var der fysisk. Det har gjort det muligt for mig at fortsætte undervisningen under mit udvekslingsophold."
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-md">
                <div className="flex flex-col h-full">
                  <div className="mb-6">
                    <div className="flex text-piano-gold">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                  <blockquote className="text-gray-600 italic mb-6 flex-grow">
                    "{testimonial.text}"
                  </blockquote>
                  <footer className="mt-auto">
                    <p className="font-medium">{testimonial.name}</p>
                  </footer>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-10 text-center">
            <a 
              href="https://dk.trustpilot.com/review/oscargilbert.dk" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-flex items-center text-piano-gold hover:underline"
            >
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.2,12.05l-2.79,2.72,0.66,3.84a0.49,0.49,0,0,1-.71.53L14,17.31l-3.39,1.79a0.49,0.49,0,0,1-.71-0.53L10.6,14.71,7.8,12a0.5,0.5,0,0,1,.28-0.85l3.86-.56,1.73-3.48a0.5,0.5,0,0,1,.9,0l1.73,3.48,3.86,0.56A0.5,0.5,0,0,1,20.2,12.05Z" />
              </svg>
              Se Flere Anmeldelser På Trustpilot
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-piano-black text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">Start din musikalske rejse i dag</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Book en gratis prøvetime og oplev forskellen med professionel klaverundervisning fra Oscar Gilbert.
          </p>
          <Button asChild size="lg" className="bg-piano-gold hover:bg-piano-gold/90 text-white">
            <Link to="/kontakt">Book Gratis Prøvetime</Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
